/**
 * AltTextCaptionForm Component
 * Form for editing alt text and caption for images
 * Features:
 * - Alt text input (required for accessibility)
 * - Caption input (optional)
 * - Character count
 * - Accessibility reminder
 */

import { cn } from '@/lib/utils'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import {
  AlertTriangle,
  Type,
  MessageSquare,
} from 'lucide-react'

// ============================================
// Types
// ============================================

export interface AltTextCaptionFormProps {
  altText: string
  caption: string
  onAltTextChange: (value: string) => void
  onCaptionChange: (value: string) => void
  disabled?: boolean
  showWarning?: boolean
  className?: string
}

// ============================================
// Constants
// ============================================

const ALT_TEXT_MAX_LENGTH = 125
const CAPTION_MAX_LENGTH = 500

// ============================================
// AltTextCaptionForm Component
// ============================================

export function AltTextCaptionForm({
  altText,
  caption,
  onAltTextChange,
  onCaptionChange,
  disabled = false,
  showWarning = false,
  className,
}: AltTextCaptionFormProps) {
  const altTextLength = altText.length
  const captionLength = caption.length

  return (
    <div className={cn('space-y-4', className)}>
      {/* Alt Text Input */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label htmlFor="alt-text" className="flex items-center gap-2">
            <Type className="h-4 w-4 text-neutral-400" />
            Alt Text
            {showWarning && (
              <span className="flex items-center gap-1 text-amber-600 dark:text-amber-400 text-xs">
                <AlertTriangle className="h-3.5 w-3.5" />
                Required for accessibility
              </span>
            )}
          </Label>
          <span
            className={cn(
              'text-xs',
              altTextLength > ALT_TEXT_MAX_LENGTH
                ? 'text-red-500'
                : altTextLength > ALT_TEXT_MAX_LENGTH * 0.8
                  ? 'text-amber-500'
                  : 'text-neutral-400'
            )}
          >
            {altTextLength}/{ALT_TEXT_MAX_LENGTH}
          </span>
        </div>
        <Input
          id="alt-text"
          type="text"
          placeholder="Describe the image for screen readers..."
          value={altText}
          onChange={(e) => onAltTextChange(e.target.value)}
          disabled={disabled}
          maxLength={ALT_TEXT_MAX_LENGTH + 50} // Allow some overflow for editing
          className={cn(
            'rounded-xl',
            showWarning && !altText && 'border-amber-400 focus:ring-amber-500'
          )}
        />
        <p className="text-xs text-neutral-500 dark:text-neutral-400">
          Describe the image content and purpose. Keep it concise but meaningful.
        </p>
      </div>

      {/* Caption Input */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label htmlFor="caption" className="flex items-center gap-2">
            <MessageSquare className="h-4 w-4 text-neutral-400" />
            Caption
            <span className="text-xs text-neutral-400 font-normal">(optional)</span>
          </Label>
          <span
            className={cn(
              'text-xs',
              captionLength > CAPTION_MAX_LENGTH
                ? 'text-red-500'
                : captionLength > CAPTION_MAX_LENGTH * 0.8
                  ? 'text-amber-500'
                  : 'text-neutral-400'
            )}
          >
            {captionLength}/{CAPTION_MAX_LENGTH}
          </span>
        </div>
        <Textarea
          id="caption"
          placeholder="Add a caption that will be displayed below the image..."
          value={caption}
          onChange={(e) => onCaptionChange(e.target.value)}
          disabled={disabled}
          rows={2}
          maxLength={CAPTION_MAX_LENGTH + 50}
          className="rounded-xl resize-none"
        />
        <p className="text-xs text-neutral-500 dark:text-neutral-400">
          Captions appear below images and provide additional context or credit.
        </p>
      </div>

      {/* Accessibility Tips */}
      {showWarning && !altText && (
        <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
          <div className="flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
            <div className="space-y-1">
              <p className="text-sm font-medium text-amber-700 dark:text-amber-300">
                Missing Alt Text
              </p>
              <p className="text-xs text-amber-600 dark:text-amber-400">
                Images without alt text are not accessible to users with screen readers.
                Please add a descriptive alt text to make your content accessible to everyone.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Alt Text Best Practices */}
      <div className="p-3 rounded-lg bg-neutral-50 dark:bg-neutral-800/50 border border-neutral-200 dark:border-neutral-700">
        <p className="text-xs font-medium text-neutral-600 dark:text-neutral-400 mb-2">
          Alt Text Best Practices:
        </p>
        <ul className="text-xs text-neutral-500 dark:text-neutral-400 space-y-1 list-disc list-inside">
          <li>Be specific and concise (aim for 125 characters or less)</li>
          <li>Describe the image content and function</li>
          <li>Avoid phrases like "image of" or "picture of"</li>
          <li>Include text visible in the image</li>
          <li>For decorative images, leave alt text empty</li>
        </ul>
      </div>
    </div>
  )
}

export default AltTextCaptionForm
